import { useEffect, useMemo, useState } from 'react'
import { PageHeader } from '../../../components/PageHeader'
import { Avatar } from '../../../components/Avatar'
import { ReportButton } from '../../../components/ReportButton'
import { useAuth } from '../../../lib/auth'
import {
  dishDisplayName, fetchGallery, fetchSubmissionAuthors, weekLabel,
} from '../../../lib/api/weekly'
import { useMutedIds, MUTED_PLACEHOLDER } from '../../../lib/api/mutes'
import { supabase } from '../../../lib/supabase'
import type { WeeklyEventRow, WeeklySubmissionRow } from '../../../lib/database.types'
import { readError } from '../../../lib/errors'
import { BackgroundTitle } from '../../../components/BackgroundTitle'
import { useT } from '../../../i18n'

interface Props {
  eventId: string
  onBack: () => void
  onPlayAgain: () => void
  /** Open the author's public profile when their name is tapped. */
  onOpenProfile?: (profileId: string) => void
}

interface AuthorInfo { display_name: string; avatar_url: string | null }

/**
 * Final leaderboard for a past weekly event — top story is the champion,
 * everyone else fills out the ranking. Loaded fresh each time so votes
 * that came in after you last looked are reflected.
 */
export function WinnersScreen({ eventId, onBack, onPlayAgain, onOpenProfile }: Props) {
  const t = useT()
  const { profile } = useAuth()
  const { mutedIds } = useMutedIds()
  const [event, setEvent] = useState<WeeklyEventRow | null>(null)
  const [subs, setSubs] = useState<WeeklySubmissionRow[]>([])
  const [authors, setAuthors] = useState<Record<string, AuthorInfo>>({})
  const [expanded, setExpanded] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const evRes = await supabase
          .from('weekly_events').select('*').eq('id', eventId).single()
        if (evRes.error) throw evRes.error
        if (cancelled) return
        setEvent(evRes.data as WeeklyEventRow)

        // Same predicate as the voting screen: a finalized row with no
        // story is a placeholder from a hub visit, not an entrant.
        const hasContent = (s: WeeklySubmissionRow) =>
          (s.title?.trim().length ?? 0) > 0 ||
          (s.course1_text?.trim().length ?? 0) > 0
        const gallery = (await fetchGallery(eventId)).filter(s => s.is_final && hasContent(s))
        if (cancelled) return
        setSubs(gallery)
        const a = await fetchSubmissionAuthors(gallery.map(s => s.profile_id))
        if (!cancelled) setAuthors(a)
      } catch (e) {
        if (!cancelled) setError(readError(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [eventId])

  const sorted = useMemo(
    () => [...subs].sort((a, b) => b.total_votes - a.total_votes),
    [subs],
  )
  const winner = sorted[0]
  const totalVotes = sorted.reduce((s, r) => s + r.total_votes, 0)

  if (loading) {
    return (
      <div className="screen">
        <BackgroundTitle text="I'd Eat That" color="#B58615" />
        <PageHeader title={t('Results')} onBack={onBack} />
        <div className="flex-1 px-5 py-5 space-y-3">
          <div className="h-48 rounded-2xl bg-brand-elevated animate-pulse" />
          <div className="h-16 rounded-2xl bg-brand-elevated animate-pulse" />
          <div className="h-16 rounded-2xl bg-brand-elevated animate-pulse" />
        </div>
      </div>
    )
  }

  if (!winner) {
    return (
      <div className="screen">
        <BackgroundTitle text="I'd Eat That" color="#B58615" />
        <PageHeader title={t('Results')} onBack={onBack} />
        <div className="flex-1 flex items-center justify-center px-5 text-center">
          <div>
            <h2 className="font-display text-xl font-bold text-brand-text">
              {t('No winner yet')}
            </h2>
            <p className="text-sm text-brand-muted mt-1 max-w-xs">
              {error ?? t('No finalised stories have votes for this week.')}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="screen">
      <BackgroundTitle text="I'd Eat That" color="#B58615" />
      <PageHeader
        title={event ? weekLabel(event.week_start) : t('Results')}
        subtitle={sorted.length === 1
          ? t('1 story · {votes} total votes', { votes: totalVotes })
          : t('{n} stories · {votes} total votes', { n: sorted.length, votes: totalVotes })}
        onBack={onBack}
      />

      {/* Flat list of all submissions for the week, ordered by vote
          count. Each row expands to read the full story — no trophy
          spotlight, no champion glow. */}
      <div className="px-5 py-4 space-y-2 overflow-y-auto pb-28">
        {sorted.map((sub) => {
          const a = authors[sub.profile_id]
          const isOwn = sub.profile_id === profile?.id
          const muted = mutedIds.has(sub.profile_id)
          const isExpanded = expanded === sub.id
          return (
            <div
              key={sub.id}
              className={`iet-gold border-2 border-game-iet-dark
                          rounded-2xl shadow-card overflow-hidden transition-all
                          ${isOwn ? 'ring-2 ring-brand-lime-dark/40' : ''}`}
            >
              <button
                onClick={() => setExpanded(isExpanded ? null : sub.id)}
                className="w-full p-4 flex items-center gap-3 text-left active:scale-[0.99] transition-transform"
              >
                <Avatar name={a?.display_name ?? 'Player'} avatarUrl={a?.avatar_url} size="md" className="shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-brand-text truncate">
                      {muted ? MUTED_PLACEHOLDER : dishDisplayName(sub, a)}
                    </span>
                    {isOwn && (
                      <span className="text-[10px] text-brand-lime-dark font-semibold shrink-0">{t('You')}</span>
                    )}
                  </div>
                  <p className="text-xs text-brand-muted truncate">
                    by{' '}
                    {onOpenProfile && !isOwn ? (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onOpenProfile(sub.profile_id) }}
                        className="underline underline-offset-2 hover:text-brand-text
                                   active:text-brand-text"
                      >
                        {a?.display_name ?? 'Player'}
                      </button>
                    ) : (
                      a?.display_name ?? 'Player'
                    )}
                  </p>
                </div>
                <span className="text-xs font-semibold text-game-iet-dark shrink-0">
                  {sub.total_votes} 👍
                </span>
              </button>

              {isExpanded && sub.course1_text.trim() && (
                <div className="border-t border-game-iet-dark/40 px-4 py-3 bg-white/40">
                  <p className="text-sm text-brand-text leading-relaxed whitespace-pre-wrap">
                    "{muted ? MUTED_PLACEHOLDER : sub.course1_text}"
                  </p>
                  {!isOwn && (
                    <div className="flex justify-end mt-2">
                      <ReportButton submissionId={sub.id} />
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>

      <div className="floating-actions">
        <button onClick={onPlayAgain} className="btn-iet">
          {t('Play a Live Game')}
        </button>
      </div>
    </div>
  )
}
